/**
 * 手机端客户端（P3）：Bearer 设备令牌，不走 Cookie。
 * 令牌只存在 localStorage；配对一次性完成，可随时在 Web 撤销。
 */

import type { HarnessEvent } from '@xiaotianshu/shared/harness';
import type {
  AgentBook, AgentDocument, AgentDraft, AgentIdea, AgentImage, AgentMessage, AgentThread,
} from './agentClient';

export interface MobileChapter {
  id: string;
  volume_id: string;
  title: string;
  summary: string | null;
  word_count: number | null;
  chapter_number: number | null;
  status: string;
}

export interface MobileLibrary {
  book: { id: string; title: string; word_count: number | null };
  volumes: Array<{ id: string; title: string; description: string | null; chapters: MobileChapter[] }>;
  library: {
    characters: Array<{ id: string; name: string; age: string | null; strength: string | null; description: string | null }>;
    factions: Array<{ id: string; name: string; description: string | null }>;
    settings: Array<{ id: string; title: string; content: string | null }>;
    items: Array<{ id: string; name: string; type: string; description: string | null }>;
  };
}

const TOKEN_KEY = 'xts_agent_token';

export function getMobileToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? '';
}

export function setMobileToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearMobileToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

async function mobileJson<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getMobileToken();
  const res = await fetch(path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const message = data && typeof data === 'object' && 'error' in data
      ? String((data as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data as T;
}

export async function pairDevice(code: string, name: string): Promise<{ token: string; device: { id: string; name: string } }> {
  const res = await fetch('/api/agent/pairing/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: code.trim().toUpperCase(), name, platform: 'android' }),
  });
  const text = await res.text();
  const data = JSON.parse(text) as { token?: string; device?: { id: string; name: string }; error?: string };
  if (!res.ok || !data.token) throw new Error(data.error || '配对失败');
  setMobileToken(data.token);
  return { token: data.token, device: data.device ?? { id: '', name } };
}

export async function mobileBooks(): Promise<AgentBook[]> {
  return (await mobileJson<{ books: AgentBook[] }>('/api/agent/books')).books;
}

export interface MobileNotification {
  id: string;
  eventId: string;
  threadId: string | null;
  kind: string;
  title: string;
  deepLink: string;
  createdAt: string | null;
}

export async function mobileNotifications(): Promise<MobileNotification[]> {
  return (await mobileJson<{ notifications: MobileNotification[] }>('/api/agent/notifications')).notifications;
}

export function openMobileNotificationStream(onNotification: (n: MobileNotification) => void): () => void {
  const token = getMobileToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  const es = new EventSource(`/api/agent/notifications/stream${query}`);
  es.addEventListener('notification', (raw) => {
    try { onNotification(JSON.parse((raw as MessageEvent).data) as MobileNotification); } catch { /* ignore */ }
  });
  return () => es.close();
}

export async function mobileLibrary(bookId: string): Promise<MobileLibrary> {
  return mobileJson<MobileLibrary>(`/api/agent/books/${encodeURIComponent(bookId)}/library`);
}

export async function mobileChapter(bookId: string, chapterId: string): Promise<MobileChapter & { content: string | null }> {
  return (await mobileJson<{ chapter: MobileChapter & { content: string | null } }>(
    `/api/agent/books/${encodeURIComponent(bookId)}/chapters/${encodeURIComponent(chapterId)}`,
  )).chapter;
}

export async function mobileAnswerApproval(threadId: string, requestId: string, decision: 'allow' | 'deny'): Promise<void> {
  await mobileJson(`/api/agent/threads/${encodeURIComponent(threadId)}/approvals/${encodeURIComponent(requestId)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision }),
  });
}

export async function mobileRejectDraft(id: string, comment: string): Promise<void> {
  await mobileJson(`/api/agent/drafts/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
}

export async function mobileEnsureThread(bookId: string): Promise<AgentThread> {
  return (await mobileJson<{ thread: AgentThread }>('/api/agent/threads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bookId }),
  })).thread;
}

export async function mobileMessages(threadId: string): Promise<AgentMessage[]> {
  return (await mobileJson<{ messages: AgentMessage[] }>(`/api/agent/threads/${encodeURIComponent(threadId)}/messages`)).messages;
}

export async function mobileSend(
  threadId: string,
  text: string,
  images?: AgentImage[],
  documents?: AgentDocument[],
): Promise<void> {
  await mobileJson(`/api/agent/threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      ...(images && images.length > 0 ? { images } : {}),
      ...(documents && documents.length > 0 ? { documents } : {}),
    }),
  });
}

export async function mobileIdeas(bookId: string): Promise<AgentIdea[]> {
  return (await mobileJson<{ ideas: AgentIdea[] }>(`/api/agent/ideas?bookId=${encodeURIComponent(bookId)}`)).ideas;
}

export async function mobileApproveIdea(id: string): Promise<void> {
  await mobileJson(`/api/agent/ideas/${encodeURIComponent(id)}/approve`, { method: 'POST' });
}

export async function mobileRejectIdea(id: string, reason: string): Promise<void> {
  await mobileJson(`/api/agent/ideas/${encodeURIComponent(id)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function mobileDrafts(bookId: string): Promise<AgentDraft[]> {
  return (await mobileJson<{ drafts: AgentDraft[] }>(`/api/agent/drafts?bookId=${encodeURIComponent(bookId)}`)).drafts;
}

export async function mobileAcceptDraft(id: string): Promise<void> {
  await mobileJson(`/api/agent/drafts/${encodeURIComponent(id)}/accept`, { method: 'POST' });
}

export function openMobileEvents(threadId: string, onEvent: (ev: HarnessEvent) => void): () => void {
  const token = getMobileToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  const es = new EventSource(`/api/agent/threads/${encodeURIComponent(threadId)}/events${query}`);
  es.addEventListener('agent', (raw) => {
    try { onEvent(JSON.parse((raw as MessageEvent).data) as HarnessEvent); } catch { /* ignore */ }
  });
  return () => es.close();
}
